import { cx } from "@emotion/css";
import {
	default as Button,
	default as MUIIconButton,
	type ButtonProps as MuiButtonProps,
} from "@mui/material/Button";
import type { LocationDescriptor } from "history";
import type { ReactNode, Ref } from "react";

interface DefaultButtonProps extends Omit<MuiButtonProps, "ref"> {
	isLoading?: boolean;
	buttonRef?: Ref<HTMLButtonElement>;
	component?: React.ElementType;
	to?: LocationDescriptor;
}

const DefaultButton = ({
	className,
	children,
	isLoading,
	buttonRef,
	...props
}: DefaultButtonProps) => {
	return (
		<Button
			ref={buttonRef}
			className={cx("button", className)}
			loading={isLoading}
			{...props}
		>
			{children}
		</Button>
	);
};

interface NewButtonProps extends DefaultButtonProps {
	variant?: "outlined" | "text" | "contained";
}

export const NewButton = ({ variant, ...props }: NewButtonProps) => {
	if (variant === "outlined") {
		return <OutlinedPrimaryButton {...props} />;
	}

	if (variant === "text") {
		return <TextPrimaryButton {...props} />;
	}

	return <PrimaryButton {...props} />;
};

export const PrimaryButton = ({ className, ...props }: DefaultButtonProps) => {
	return (
		<DefaultButton className={cx(className, "btn primary-btn")} {...props} />
	);
};

export const GreyButton = ({ className, ...props }: DefaultButtonProps) => {
	return <DefaultButton className={cx(className, "btn grey-btn")} {...props} />;
};

export const SoftPrimaryButton = ({
	className,
	...props
}: DefaultButtonProps) => {
	return (
		<DefaultButton
			className={cx(className, "btn soft-primary-btn")}
			{...props}
		/>
	);
};

export const ErrorButton = ({ className, ...props }: DefaultButtonProps) => {
	return (
		<DefaultButton className={cx(className, "btn error-btn")} {...props} />
	);
};

export const OutlinedErrorButton = ({
	className,
	...props
}: DefaultButtonProps) => {
	return (
		<DefaultButton
			className={cx(className, "btn error-outlined-btn")}
			{...props}
		/>
	);
};

export const OutlinedPrimaryButton = ({
	className,
	...props
}: DefaultButtonProps) => {
	return (
		<DefaultButton
			className={cx(className, "btn primary-outlined-btn")}
			{...props}
		/>
	);
};

export const TextPrimaryButton = ({
	className,
	...props
}: DefaultButtonProps) => {
	return (
		<DefaultButton
			className={cx(className, "btn primary-text-btn")}
			{...props}
		/>
	);
};

export const TextButton = ({ className, ...props }: DefaultButtonProps) => {
	return <DefaultButton className={cx(className, "btn")} {...props} />;
};

export const PrimaryLinkButton = ({
	className,
	...props
}: DefaultButtonProps) => {
	return (
		<DefaultButton
			className={cx(className, "btn primary-link-btn")}
			{...props}
		/>
	);
};

export const TextBlackButton = ({
	className,
	...props
}: DefaultButtonProps) => {
	return (
		<DefaultButton
			className={cx(className, "btn black-text-btn size-16-light")}
			{...props}
		/>
	);
};

export const OutlinedTextBlackButton = ({
	className,
	...props
}: DefaultButtonProps) => {
	return (
		<DefaultButton
			className={cx(className, "btn black-outlined-btn size-16-light")}
			{...props}
		/>
	);
};

export const OutlinedTextBlueButton = ({
	className,
	...props
}: DefaultButtonProps) => {
	return (
		<DefaultButton className={cx(className, "outlined-blue-btn")} {...props} />
	);
};

interface IconButtonProps extends Omit<MuiButtonProps, "error"> {
	edge?: "start" | "end" | false;
	isActive?: boolean;
	error?: boolean;
	loading?: boolean;
	children?: ReactNode;
}

export const IconButton = ({
	className,
	children,
	isActive,
	error,
	loading,
	...props
}: IconButtonProps) => {
	return (
		<MUIIconButton
			{...props}
			loading={loading}
			className={cx(className, "icon-button", {
				"icon-button-active": isActive,
				"icon-button--error": error,
				"icon-button--disabled": props.disabled,
			})}
		>
			{!loading && children}
		</MUIIconButton>
	);
};
